import isEqual from 'lodash-es/isEqual';
import React, { useState, useEffect } from 'react';
import { List } from './List';
import { useCategorySelector } from './hooks';
import {
  isDirectlySelected,
  unmergeSiblingsKeys,
  mergeSiblingsKeys,
  removeChildrenKeys,
  removeParentKeys,
} from './utils';
import { Category } from './types';

interface CategoriesByLevelsProps {
  categoriesByLevels: Array<Array<Category>>;
  selectedKeys: Array<string>;
  multiple?: boolean;
  disabled?: boolean;
  onChange: (selectedKeys: Array<string>, isLeaf?: boolean) => void;
}

const INITIAL_SELECTED_CATEGORY_KEYS = [''];

export const CategoriesByLevels = ({
  categoriesByLevels,
  selectedKeys,
  multiple,
  disabled,
  onChange,
}: CategoriesByLevelsProps) => {
  const [selectedCategoryKeys, setSelectedCategoryKeys] = useState<Array<string>>(INITIAL_SELECTED_CATEGORY_KEYS);
  const { categoryMap, findCategoryPath, categoryStates } = useCategorySelector(
    categoriesByLevels,
    selectedKeys,
    multiple,
  );

  const handleCategoryClick = (category: Category) => {
    setSelectedCategoryKeys((selectedCategoryKeys) => {
      return [...selectedCategoryKeys.slice(0, category.level + 1), category.key];
    });
  };

  const handleCategorySelect = (category: Category) => {
    if (multiple) {
      toggleKey(category);
    } else {
      onChange([category.key], !category.childrenKeys);
    }
  };

  const toggleKey = (category: Category) => {
    const { key } = category;
    const currentlyChecked = isChecked(category);
    const selectedKeysCopy = [...selectedKeys];
    // 중복/충돌을 제거한 깨끗한 선택 keys 목록
    const cleanedKeys = removeParentKeys(
      removeChildrenKeys(unmergeSiblingsKeys(selectedKeysCopy, category, categoryMap), category, categoryMap),
      category,
      categoryMap,
    );
    // 현재 액션(선택/해제)이 반영된 상태의 선택 keys 목록
    const updatedToggledKeys = currentlyChecked
      ? cleanedKeys.filter((selectedKey) => selectedKey !== key)
      : [...cleanedKeys, key];
    // 형제 카테고리 병합 최적화가 완료된 최종 결과 keys 목록
    const finalResult = mergeSiblingsKeys(updatedToggledKeys, category, categoryMap);
    onChange(finalResult);
  };

  const isSelected = (category: Category): boolean => {
    // 네비게이션 경로 표시용 (단순히 selectedCategoryKeys에 있는지만 확인)
    if (multiple) {
      return selectedCategoryKeys.includes(category.key);
    }
    return selectedCategoryKeys.includes(category.key) || isDirectlySelected(category, selectedKeys);
  };

  const isChecked = (category: Category): boolean => {
    if (category.disabled) {
      return false;
    }
    if (multiple) {
      return categoryStates?.checkedMap.get(category.key) ?? false;
    }
    return isDirectlySelected(category, selectedKeys);
  };

  const isIndeterminate = (category: Category): boolean => {
    if (!multiple) {
      return false;
    }
    return categoryStates?.indeterminateMap.get(category.key) ?? false;
  };

  useEffect(() => {
    // MEMO: 외부 selectedKeys 변경시 selectedCategoryKeys 싱크(다중 선택일때는 싱크하지 않습니다.)
    if (!multiple) {
      if (selectedKeys.length === 0) {
        setSelectedCategoryKeys(INITIAL_SELECTED_CATEGORY_KEYS);
      } else if (isEqual(selectedCategoryKeys, INITIAL_SELECTED_CATEGORY_KEYS)) {
        setSelectedCategoryKeys(findCategoryPath);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findCategoryPath, multiple, selectedKeys]);

  return categoriesByLevels.map((list_item, i) => {
    const filteredCategories = list_item.filter(({ parentKey }) => parentKey === selectedCategoryKeys[i]);
    return (
      <List
        key={i}
        level={i + 1}
        categories={filteredCategories}
        selectedKeys={selectedKeys}
        onSelect={handleCategorySelect}
        onClick={handleCategoryClick}
        isSelected={isSelected}
        isChecked={isChecked}
        isIndeterminate={isIndeterminate}
        multiple={multiple}
        disabled={disabled}
      />
    );
  });
};
